import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import MainLayout from "./layouts/MainLayout";
import LegalAcceptance from "./pages/LegalAcceptance";
import PatientList from "./pages/PatientList";
import PatientDetail from "./pages/PatientDetail";
import { OwcProgressSpinner } from "@one/react";
import "./App.css";
import { AuthService } from "./services/AuthService";

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  const { authState, login } = useAuth();
  const [isChecking, setIsChecking] = React.useState(true);

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      setIsChecking(true);

      if (!authState.isAuthenticated) {
        // Not logged in, redirect to OIDC provider
        console.log("Not authenticated, redirecting to OIDC provider");
        try {
          await login();
        } catch (error) {
          console.error("Authentication redirect failed:", error);
        }
        return;
      }

      setIsChecking(false);
    };

    checkAuthAndRedirect();
  }, [authState.isAuthenticated, login]);

  // Show loading spinner while checking authentication
  if (isChecking && !authState.isAuthenticated) {
    return (
      <div className="owc-flex owc-justify-center owc-align-center owc-full-height">
        <OwcProgressSpinner size="large" />
      </div>
    );
  }

  if (
    authState.isAuthenticated &&
    (!authState.hasAcceptedLatestTerms || !authState.hasAcceptedLatestPrivacy)
  ) {
    // Logged in but hasn't accepted latest terms or privacy
    return <Navigate to="/legal-acceptance" replace />;
  }

  // User is authenticated and has accepted terms and privacy
  return element;
};

// Callback route to handle auth redirect with improved reliability
const CallbackPage: React.FC = () => {
  const { handleLoginCallback } = useAuth();
  const [error, setError] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(1);
  const maxAttempts = 2; // Maximum number of attempts to process the callback

  React.useEffect(() => {
    // Don't try to process more than the maximum number of attempts
    if (attempt > maxAttempts) {
      return;
    }

    const processCallback = async () => {
      console.log(
        `Starting callback processing (attempt ${attempt} of ${maxAttempts})`
      );

      try {
        // Pause briefly before processing the callback
        // This can help with timing issues related to state storage
        if (attempt > 1) {
          console.log("Waiting briefly before retry...");
          await new Promise((resolve) => setTimeout(resolve, 800));
        }

        // Parse URL parameters for diagnostics
        const urlParams = new URLSearchParams(window.location.search);
        console.log("Callback URL parameters:", {
          state: urlParams.get("state"),
          code: urlParams.get("code") ? "present" : "missing",
          error: urlParams.get("error"),
        });

        // Try to handle the login callback
        await handleLoginCallback();
        console.log("Callback processed successfully");

        // Navigate to the stored redirect URL or default to /patients
        const redirectUrl =
          sessionStorage.getItem("redirectUrl") || "/patients";
        sessionStorage.removeItem("redirectUrl"); // Clean up

        // Use React Router navigation if possible
        window.location.href = redirectUrl;
      } catch (err: unknown) {
        console.error("Login callback error:", err);

        // If this is not the last attempt, try again
        if (attempt < maxAttempts) {
          console.log(
            `Retrying callback processing (${attempt}/${maxAttempts})`
          );
          setAttempt(attempt + 1);
        } else {
          // All attempts failed, show error message
          const errorMessage =
            err instanceof Error ? err.message : "Unknown error";
          setError(`Authentication failed: ${errorMessage}. Please try again.`);

          // Log detailed diagnostics
          console.group("Final callback error details");
          console.log("Error:", err);
          console.log("State stores:", {
            localStorage: Object.keys(localStorage).filter((k) =>
              k.startsWith("oidc.")
            ),
            sessionStorage: Object.keys(sessionStorage).filter((k) =>
              k.startsWith("oidc.")
            ),
          });
          console.groupEnd();
        }
      }
    };

    processCallback();
  }, [handleLoginCallback, attempt]);

  if (error) {
    return (
      <div
        style={{
          padding: "16px",
          textAlign: "center",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <div>
          <h3>Authentication Error</h3>
          <p>{error}</p>
          <p>
            This could happen if your session expired or if there was an issue
            with the authentication process.
          </p>
        </div>
        <div style={{ marginTop: "24px" }}>
          <button
            onClick={() => (window.location.href = "/login")}
            style={{
              padding: "10px 20px",
              backgroundColor: "#3f51b5",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <OwcProgressSpinner size="large" />
      <div style={{ marginTop: "24px" }}>
        <div>
          Processing login
          {attempt > 1 ? ` (attempt ${attempt}/${maxAttempts})` : ""}, please
          wait...
        </div>
      </div>
    </div>
  );
};

function App() {
  // Initialize AuthService and track initialization state
  const [isAuthInitialized, setIsAuthInitialized] = React.useState(false);

  useEffect(() => {
    // Initialize the auth service
    AuthService.init();
    console.log("AuthService initialized");
    setIsAuthInitialized(true);

    // Extract any search parameters for debugging
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParams = urlParams.has("code") || urlParams.has("state");

    if (hasAuthParams) {
      console.log("Auth params detected in URL:", {
        code: urlParams.has("code"),
        state: urlParams.get("state"),
      });

      // If we're at the root with auth params, redirect to callback
      if (window.location.pathname === "/") {
        console.log("Redirecting to callback route with auth params");
        window.location.href = "/callback" + window.location.search;
      }
    }
  }, []);

  // Show loading until auth is initialized
  if (!isAuthInitialized) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <OwcProgressSpinner size="large" />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* The callback route for handling OIDC redirect */}
            <Route path="/callback" element={<CallbackPage />} />

            {/* Legal acceptance page */}
            <Route path="/legal-acceptance" element={<LegalAcceptance />} />

            {/* Patient routes - all are protected */}
            <Route
              path="/patients"
              element={
                <ProtectedRoute
                  element={
                    <MainLayout>
                      <PatientList />
                    </MainLayout>
                  }
                />
              }
            />

            <Route
              path="/patients/:patientId"
              element={
                <ProtectedRoute
                  element={
                    <MainLayout>
                      <PatientDetail />
                    </MainLayout>
                  }
                />
              }
            />

            {/* Redirect root to patients page */}
            <Route path="/" element={<Navigate to="/patients" />} />

            {/* Redirect old login page to patients page (which will handle auth) */}
            {/* Redirect to patients which will trigger auth flow if needed */}
            <Route path="/login" element={<Navigate to="/patients" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
